import type { ReactNode } from 'react';
import { cn } from './lib/cn';

/*
 * Named for what the badge says, not for the colour it comes out. A caller
 * that asks for a hue pins the palette in place and has to be revisited for
 * every restyle and for the dark theme; one that asks for a meaning does not.
 */
export type BadgeTone =
  | 'neutral'
  | 'danger'
  | 'caution'
  | 'warning'
  | 'success'
  | 'info'
  | 'accent';

export type BadgeProps = {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
};

const TONES: Record<BadgeTone, string> = {
  neutral: 'sk:bg-fill-secondary sk:text-fg-secondary',
  danger: 'sk:bg-danger-bg sk:text-danger',
  caution: 'sk:bg-caution-bg sk:text-caution',
  warning: 'sk:bg-warning-bg sk:text-warning',
  success: 'sk:bg-success-bg sk:text-success',
  info: 'sk:bg-info-bg sk:text-info',
  accent: 'sk:bg-accent-bg sk:text-accent',
};

/** Status pill — service levels, attention flags, anything read-only. */
export function Badge({ children, tone = 'neutral', className }: BadgeProps) {
  return (
    <span
      className={cn(
        'sk:inline-flex sk:items-center sk:gap-1.5 sk:whitespace-nowrap sk:rounded-full',
        'sk:inset-shadow-hairline sk:h-6 sk:px-2.5 sk:text-dense sk:font-medium sk:tracking-dense',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
